const { LOCAL_CERT_DOMAINS } = require('./local-domains');

// ---------------------------------------------------------------------------
// Faro collector wiring
//
// Two consumers share these values: the browser SDK (sdk.ts) and the Next.js
// rewrite rule (next.config.js). They must agree, so the values live here and
// not in either consumer. Routing is keyed off ENVIRONMENT_STATUS: every
// locally-run status contains the substring 'local' (see config/type.ts
// Environment), every GitHub-Actions/IDC-deployable one does not — so any
// deployed build sends to the obs collector, and events stay separable there
// because sdk.ts stamps SettingConfig.environment as app.environment.
// `FARO_COLLECTOR_URL` is an optional build-time override so any build/dev-run
// can point at any collector without a code change.
// ---------------------------------------------------------------------------
const isDeployedBuild = !(process.env.ENVIRONMENT_STATUS || 'local').includes('local');

/**
 * Faro proxy wiring shared by the browser SDK and the Next.js rewrite rule.
 *
 * - `source`            — same-origin path the browser POSTs to.
 * - `destination`       — where the Next.js server forwards that path.
 *                         `FARO_COLLECTOR_URL` wins if set; otherwise every
 *                         deployed build (prod, dev망, fanza, sales) routes to
 *                         the obs host's internal NIC and local runs hit the
 *                         loopback docker compose stack.
 * - `sessionSampleRate` — fraction of sessions sampled by the Faro SDK.
 *
 * @returns {{ source: string, destination: string, sessionSampleRate: number }}
 */
const getFaroProxyConfig = () => ({
  source: '/faro/collect',
  destination:
    process.env.FARO_COLLECTOR_URL ||
    (isDeployedBuild ? 'http://10.1.1.93:12347/collect' : 'http://localhost:12347/collect'),
  // 5% session sampling: a sampled session sends ALL its events (funnels/ratios
  // stay coherent), so absolute counts are ~1/20 of reality; QoE ratios are unaffected.
  sessionSampleRate: 0.05,
});

/**
 * @returns {import('next').NextConfig['env']}
 */
const getEnvConfig = () => ({
  ENVIRONMENT_STATUS: process.env.ENVIRONMENT_STATUS,
  SHORT_SHA: process.env.SHORT_SHA,
  NEXT_PUBLIC_SHORT_SHA: process.env.SHORT_SHA,
});

/**
 * @param {import('next').NextConfig} nextConfig
 * @param {{ cdnPath: string }} options
 * @returns {import('next').NextConfig}
 */
const applyAssetPrefix = (nextConfig, { cdnPath }) => {
  if (!process.env.CDN_URL) {
    return nextConfig;
  }

  nextConfig.assetPrefix = new URL(`${process.env.CDN_PREFIX}/${cdnPath}`, process.env.CDN_URL).toString();
  return nextConfig;
};

/**
 * @param {import('next').NextConfig} nextConfig
 * @returns {import('next').NextConfig}
 */
const applyOutputMode = (nextConfig) => {
  nextConfig.output = 'standalone';
  if (process.env.OUTPUT_MODE) {
    nextConfig.output = process.env.OUTPUT_MODE;
  }

  return nextConfig;
};

/**
 * @returns {NonNullable<import('next').NextConfig['images']>['remotePatterns']}
 */
const getImageRemotePatterns = () => [
  { protocol: 'https', hostname: 'cdn.bktv.kr', port: '', pathname: '/**' },
  { protocol: 'https', hostname: 'cdn.pandalive.co.kr', port: '', pathname: '/**' },
];

/**
 * @returns {NonNullable<import('next').NextConfig['allowedDevOrigins']>}
 */
const getAllowedDevOrigins = () => [...LOCAL_CERT_DOMAINS];

/**
 * @param {{ project: string }} options
 * @returns {import('@sentry/nextjs').SentryBuildOptions}
 */
const createSentryConfig = ({ project }) => ({
  org: 'doubledev',
  project,
  authToken: process.env.SENTRY_AUTH_TOKEN,
  silent: !process.env.CI,
  widenClientFileUpload: true,
  reactComponentAnnotation: { enabled: true },
  tunnelRoute: undefined,
});

/**
 *
 * @param {import('next').NextConfig} nextConfig
 * @returns {import('next').NextConfig}
 */
const applyCacheAndCompile = (nextConfig) => {
  nextConfig.cacheComponents = true;
  nextConfig.reactCompiler = true;
  nextConfig.poweredByHeader = false;
  // Version-skew protection: deploymentId must identify a *build*, not a
  // commit. AWS and IDC build the same SHA with different node_modules
  // layouts (hoisted vs isolated), so their Turbopack module ids diverge; a
  // shared id let the router accept cross-DC RSC payloads into one module
  // registry ("module factory is not available"). The per-target suffix —
  // 'a' = AWS workflow (sets OVERRIDE_CDN_PATH), 'i' = v1 IDC (doesn't) —
  // makes the skew gate reject foreign payloads with a full-page navigation.
  if (process.env.SHORT_SHA) {
    const buildTarget = process.env.OVERRIDE_CDN_PATH ? 'a' : 'i';
    nextConfig.deploymentId = `${process.env.SHORT_SHA}-${buildTarget}`;
  }
  return nextConfig;
};

/**
 * Applies the edge cache-control override to every route except `api`/`_next` and any caller-supplied
 * top-level path segments.
 *
 * NOTE: route groups (e.g. `(minimal)`) cannot be targeted directly — Next `headers()` matches URL
 * pathnames, and route-group folders never appear in the URL. To exclude a group, pass its actual
 * subroutes via `excludePaths` (e.g. `['loginCallback', 'ageCallback']`).
 *
 * @param {import('next').NextConfig} nextConfig
 * @param {{ excludePaths?: string[] }} [options] - extra top-level path segments to exclude.
 * @returns {import('next').NextConfig}
 */
const applyEdgeCacheHeaders = (nextConfig, { excludePaths = [] } = {}) => {
  const exclusions = ['api', '_next', ...excludePaths].join('|');
  nextConfig.headers = async () => [
    {
      source: `/((?!${exclusions}).*)`,
      headers: [{ key: 'Cache-Control', value: 'public, s-maxage=21600, max-age=0, stale-while-revalidate=21600' }],
    },
  ];
  return nextConfig;
};

const LIVE_ASP_REWRITE = {
  source: '/resource/js/asp/liveAsp.js',
  destination: 'https://api.pandalive.co.kr/resource/js/asp/liveAsp.js',
};

/**
 * Proxies the legacy `/resource/js/asp/liveAsp.js` path to the shared CDN origin
 * so partner sites embedding our WASP/MASP iframe keep working without each
 * Next.js app shipping its own copy of the script.
 *
 * @param {import('next').NextConfig} nextConfig
 * @returns {import('next').NextConfig}
 */
const applyLiveAspRewrite = (nextConfig) => {
  const existing = nextConfig.rewrites;
  nextConfig.rewrites = async () => {
    const prev = typeof existing === 'function' ? await existing() : null;
    if (!prev) return [LIVE_ASP_REWRITE];
    if (Array.isArray(prev)) return [...prev, LIVE_ASP_REWRITE];
    return {
      beforeFiles: prev.beforeFiles ?? [],
      afterFiles: [...(prev.afterFiles ?? []), LIVE_ASP_REWRITE],
      fallback: prev.fallback ?? [],
    };
  };
  return nextConfig;
};

/**
 * Canonical-host 308 redirect for the fanza PROD apps: apex `pandalive.co.jp` → the app's
 * canonical host (`www.pandalive.co.jp` for PC, `m.pandalive.co.jp` for mobile).
 *
 * Only added for the production fanza env (`production-fanza` / `m-production-fanza`); dev/local
 * hosts (`dev.` / `local-dev.`) never match the anchored apex rule anyway, so dev is untouched.
 *
 * Why `redirects()` and not middleware: under the custom standalone `scripts/server.js`, Next
 * middleware does NOT run, but `next.config` `redirects()` ARE evaluated by Next's request handler
 * (`getRequestHandler`). A 308 is non-200, so server.js never writes it to the html-cache (it
 * re-evaluates every apex hit). Requires CloudFront to forward the viewer `Host` to the origin —
 * it already does, since the server.js html-cache keys on `host`.
 *
 * Next anchors `has.value` as `^…$`, so `pandalive\\.co\\.jp` matches the apex host EXACTLY —
 * never `www.`/`m.` — which is what prevents a redirect loop on the canonical host.
 *
 * @param {import('next').NextConfig} nextConfig
 * @param {{ canonicalHost: string }} options
 * @returns {import('next').NextConfig}
 */
const applyFanzaCanonicalRedirect = (nextConfig, { canonicalHost }) => {
  const env = process.env.ENVIRONMENT_STATUS;
  if (env !== 'production-fanza' && env !== 'm-production-fanza') {
    return nextConfig;
  }

  const apexRedirect = {
    source: '/:path*',
    has: [{ type: 'host', value: 'pandalive\\.co\\.jp' }],
    destination: `https://${canonicalHost}/:path*`,
    permanent: true,
  };

  const existingRedirects = nextConfig.redirects;
  nextConfig.redirects = async () => {
    const prev = typeof existingRedirects === 'function' ? await existingRedirects() : [];
    return [apexRedirect, ...(Array.isArray(prev) ? prev : [])];
  };

  return nextConfig;
};

module.exports = {
  applyAssetPrefix,
  applyEdgeCacheHeaders,
  applyFanzaCanonicalRedirect,
  applyLiveAspRewrite,
  applyOutputMode,
  createSentryConfig,
  getAllowedDevOrigins,
  getEnvConfig,
  getImageRemotePatterns,
  applyCacheAndCompile,
  getFaroProxyConfig,
};
